import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Grid, Heading, Text, Select, Input, InputGroup, InputLeftElement, Icon, IconButton, Spinner, Button,
} from '@chakra-ui/react';
import { FiSearch, FiEye, FiPlus } from 'react-icons/fi';
import useAuth from 'hooks/useAuth';
import {
  getInboxOptions, getInboxSummary, getRequests, READ_ROLES, EDIT_ROLES,
  TYPE_LABEL, STATUS, CHANNEL_LABEL, PRIORITY, PAYMENT, documentLabel, formatDate, daysSince,
} from 'api/administrador/documentRequests';
import { ChipGroup, StatusPill } from 'components/monitoring/ai/common';
import AiPagination from 'components/monitoring/ai/AiPagination';
import RequestDrawer from 'components/document-requests/RequestDrawer';
import NewRequestModal from 'components/document-requests/NewRequestModal';

/**
 * Solicitudes de documentos (Ventanilla Klazia, fase 1). Reemplaza el Drive de solicitudes del área administrativa.
 */

const PAGE_SIZE = 20;
const COLS = '110px 2fr 1.5fr 1fr 1.1fr 0.9fr 1.2fr 1fr 56px';

function Waiting({ request }) {
  const closed = ['emitida', 'no_cumple', 'abandonada'].includes(request.status);
  if (closed) return <Text fontSize="sm" color="gray.600">{formatDate(request.requestedAt)}</Text>;
  const days = daysSince(request.requestedAt);
  // El área promete de 5 a 8 días hábiles: desde 8 días calendario se marca en rojo.
  const color = days >= 8 ? 'red.600' : days >= 5 ? 'orange.500' : 'gray.700';
  return (
    <Box>
      <Text fontSize="sm" color="gray.600">{formatDate(request.requestedAt)}</Text>
      <Text fontSize="xs" fontWeight="600" color={color}>{days === 0 ? 'hoy' : `hace ${days} ${days === 1 ? 'día' : 'días'}`}</Text>
    </Box>
  );
}

export default function SolicitudesDocumentos() {
  const { user } = useAuth();
  const role = user?.role?.type;
  const isAllowed = READ_ROLES.includes(role);
  const canEdit = EDIT_ROLES.includes(role);

  const [filters, setFilters] = useState({
    status: '', type: '', channel: '', assignedTo: '', order: 'recientes', search: '', page: 1,
  });
  const [searchText, setSearchText] = useState('');
  const [options, setOptions] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isAllowed) return;
    getInboxOptions().then(setOptions).catch(() => setOptions(null));
  }, [isAllowed]);

  // Búsqueda con pequeña espera para no consultar en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchText ? f : { ...f, search: searchText, page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  const base = useMemo(
    () => ({ type: filters.type, channel: filters.channel, assignedTo: filters.assignedTo, search: filters.search }),
    [filters.type, filters.channel, filters.assignedTo, filters.search],
  );

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      const [s, list] = await Promise.all([
        getInboxSummary(base),
        getRequests({ ...base, status: filters.status, order: filters.order, page: filters.page, pageSize: PAGE_SIZE }),
      ]);
      setSummary(s);
      setRows(list?.data || []);
      setPagination(list?.pagination || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[solicitudes] error cargando la bandeja:', e);
    } finally {
      setLoading(false);
    }
  }, [base, filters.status, filters.order, filters.page]);

  useEffect(() => { if (isAllowed) load(false); }, [isAllowed, load]);

  if (!isAllowed) {
    return (
      <Box p={8}>
        <Text color="gray.500">Esta vista está disponible solo para el área administrativa.</Text>
      </Box>
    );
  }

  const set = (name) => (e) => setFilters((f) => ({ ...f, [name]: e.target.value, page: 1 }));
  const count = (s) => (summary ? (s ? summary.byStatus?.[s] ?? 0 : summary.total) : null);
  const chip = (s, label) => ({ value: s, label: count(s) === null ? label : `${label} · ${count(s).toLocaleString('es-CO')}` });
  const statusChips = [chip('', 'Todas'), ...Object.keys(STATUS).map((s) => chip(s, STATUS[s].label))];

  return (
    <Box p={{ base: 3, md: 6 }}>
      <Flex mb={5} justify="space-between" align="flex-start" gap={4} wrap="wrap">
        <Box>
          <Heading fontSize="22px" fontWeight="700" letterSpacing="-.01em">Solicitudes de documentos</Heading>
          <Text color="gray.500" fontSize="14px" mt={1}>
            Constancias, certificados y demás trámites que piden estudiantes y exestudiantes. Cada solicitud tiene su número y su historial.
          </Text>
        </Box>
        {canEdit && (
          <Button leftIcon={<FiPlus />} bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} borderRadius="10px" onClick={() => setCreating(true)}>
            Nueva solicitud
          </Button>
        )}
      </Flex>

      <Box mb={4}>
        <ChipGroup
          ariaLabel="Estado"
          options={statusChips}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v, page: 1 }))}
        />
      </Box>

      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px">
        <Flex px={5} py={4} gap={3} align="center" wrap="wrap">
          <InputGroup w={{ base: '100%', md: '320px' }}>
            <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
            <Input
              aria-label="Buscar por número, nombre, documento, código o celular"
              placeholder="Número, nombre, documento, código o celular"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              borderRadius="8px"
            />
          </InputGroup>
          <Select aria-label="Tipo" w={{ base: '100%', sm: '170px' }} borderRadius="8px" value={filters.type} onChange={set('type')}>
            <option value="">Todos los tipos</option>
            {(options?.types || Object.keys(TYPE_LABEL)).map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
          </Select>
          <Select aria-label="Canal" w={{ base: '100%', sm: '170px' }} borderRadius="8px" value={filters.channel} onChange={set('channel')}>
            <option value="">Todos los canales</option>
            {(options?.channels || Object.keys(CHANNEL_LABEL)).map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c] || c}</option>)}
          </Select>
          <Select aria-label="Responsable" w={{ base: '100%', sm: '190px' }} borderRadius="8px" value={filters.assignedTo} onChange={set('assignedTo')}>
            <option value="">Todos los responsables</option>
            <option value="none">Sin asignar</option>
            {(options?.assignees || []).map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
          </Select>
          <Select aria-label="Orden" w={{ base: '100%', sm: '190px' }} borderRadius="8px" value={filters.order} onChange={set('order')}>
            <option value="recientes">Más recientes primero</option>
            <option value="antiguas">Orden de llegada</option>
          </Select>
        </Flex>

        <Box overflowX="auto">
          <Box minW="1040px">
            <Grid templateColumns={COLS} px={5} py={3} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase" gap={3}>
              <Text>Número</Text><Text>Persona</Text><Text>Documento</Text><Text>Canal</Text><Text>Estado</Text>
              <Text>Pago</Text><Text>Responsable</Text><Text>Solicitada</Text><Text />
            </Grid>

            {loading && rows.length === 0 && <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>}
            {!loading && rows.length === 0 && (
              <Text textAlign="center" py={10} color="gray.500" fontSize="sm">
                {summary?.total === 0 && !filters.search
                  ? 'Todavía no hay solicitudes. Crea la primera con «Nueva solicitud».'
                  : 'No hay solicitudes con esos filtros. Prueba con otra búsqueda o quita filtros.'}
              </Text>
            )}
            {rows.map((r) => {
              const st = STATUS[r.status];
              const pay = PAYMENT[r.paymentStatus];
              return (
                <Grid
                  key={r.id} templateColumns={COLS} px={5} py={3} gap={3} alignItems="center" borderTop="1px solid" borderColor="gray.100"
                  cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => setOpenId(r.id)}
                >
                  <Text fontSize="sm" fontFamily="mono" color="gray.700">{r.number}</Text>
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight="600" isTruncated>{r.student?.name || r.requesterName || 'Sin nombre'}</Text>
                    <Text fontSize="xs" color="gray.500" fontFamily="mono" isTruncated>
                      {r.student ? `Código ${r.student.id}` : 'No ligada a estudiante'}
                      {r.requesterDocument ? ` · ${r.requesterDocument}` : ''}
                    </Text>
                  </Box>
                  <Box minW={0}>
                    <Text fontSize="sm" isTruncated>{documentLabel(r)}</Text>
                    {r.priority !== 'baja' && <StatusPill label={`Prioridad ${PRIORITY[r.priority]?.label.toLowerCase()}`} tone={PRIORITY[r.priority]?.tone} size="10px" />}
                  </Box>
                  <Text fontSize="sm" color="gray.700" isTruncated>{CHANNEL_LABEL[r.channel] || r.channel}</Text>
                  <Box>{st ? <StatusPill label={st.label} tone={st.tone} /> : r.status}</Box>
                  <Box>{pay ? <StatusPill label={pay.label} tone={pay.tone} /> : '—'}</Box>
                  <Text fontSize="sm" color={r.assignedTo ? 'gray.700' : 'gray.400'} isTruncated>{r.assignedTo?.name || 'Sin asignar'}</Text>
                  <Waiting request={r} />
                  <Flex justify="flex-end">
                    <IconButton
                      aria-label={`Ver la solicitud ${r.number}`} icon={<FiEye />} size="sm" variant="outline" borderRadius="8px"
                      onClick={(e) => { e.stopPropagation(); setOpenId(r.id); }}
                    />
                  </Flex>
                </Grid>
              );
            })}
          </Box>
        </Box>

        <AiPagination pagination={pagination} noun="solicitudes" onPageChange={(page) => setFilters((f) => ({ ...f, page }))} />
      </Box>

      <RequestDrawer
        requestId={openId}
        isOpen={!!openId}
        onClose={() => setOpenId(null)}
        options={options}
        canEdit={canEdit}
        onChanged={() => load(true)}
      />

      <NewRequestModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        options={options}
        onCreated={(r) => { load(true); if (r?.id) setOpenId(r.id); }}
      />
    </Box>
  );
}
